import React, { useState, useEffect } from 'react';
import { FaTimes, FaSave, FaSpinner, FaReceipt } from 'react-icons/fa';

const API_URL = '/api';

const initialFormData = {
  tax_code: '',
  tax_type: '',
  tax_name: '',
  description: '',
  rate: '0.00',
  country: '',
  is_default: false,
  status: 'Active', // Should be converted to boolean/bit in backend
  created_by: 1, // Placeholder, will be replaced by actual user ID
};

const TaxFormModal = ({ open, onClose, onSubmit, initialData, isEdit, loading }) => {
  const [formData, setFormData] = useState(initialFormData);
  const [currentUser, setCurrentUser] = useState(null);
  const [isLoadingCode, setIsLoadingCode] = useState(false);

  // Fetch current user
  useEffect(() => {
    fetch(`${API_URL}/current-user`)
      .then(res => res.json())
      .then(data => {
        if (data.user) {
          setCurrentUser(data.user);
        }
      })
      .catch(error => {
        console.error('Error fetching current user:', error);
      });
  }, []);

  useEffect(() => {
    if (open) {
      if (isEdit && initialData) {
        setFormData({
          tax_code: initialData.TaxCode || '',
          tax_type: initialData.TaxType || '',
          tax_name: initialData.TaxName || '',
          description: initialData.Description || '',
          rate: initialData.Rate?.toString() || '0.00',
          country: initialData.Country || '',
          // Backend returns boolean, form uses string for checkbox
          is_default: initialData.IsDefault === true || initialData.IsDefault === 1,
          // Backend returns boolean, form uses string for select
          status: (initialData.Status === true || initialData.Status === 1) ? 'Active' : 'Inactive',
          created_by: initialData.CreatedBy || 1,
        });
      } else {
        // Only fetch new code if creating and code is empty or default
        if (!formData.tax_code || formData.tax_code === initialFormData.tax_code) {
          fetchNextCode();
        }
        setFormData(prev => ({
          ...prev,
          is_default: false,
          status: 'Active',
          created_by: currentUser?.UserID || 1,
        }));
      }
    }
  }, [open, initialData, isEdit, currentUser]); // Added currentUser to dependencies

  const fetchNextCode = async () => {
    setIsLoadingCode(true);
    try {
      const response = await fetch(`${API_URL}/taxlatestcode`);
      const data = await response.json();
      setFormData(prev => ({ ...prev, tax_code: data.nextCode }));
    } catch (error) {
      console.error('Error fetching next tax code:', error);
    } finally {
      setIsLoadingCode(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const dataToSend = {
      ...formData,
      // Convert status string back to boolean/bit for backend if needed, or let backend handle it
      status: formData.status === 'Active' ? 1 : 0,
      // Convert is_default boolean back to bit for backend
      is_default: formData.is_default ? 1 : 0,
      created_by: currentUser?.UserID || 1,
      updated_by: currentUser?.UserID || 1, // Add updated_by for consistency
    };
    onSubmit(dataToSend);
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 p-2 sm:p-4">
    <div className="bg-[#E5E1DA] rounded-2xl shadow-2xl w-full max-w-2xl p-4 sm:p-8 relative overflow-y-auto max-h-[90vh]">
        <button className="absolute top-4 right-4 text-[#2b6777] hover:text-[#52ab98]" onClick={onClose}><FaTimes size={20} /></button>
        <h2 className="text-2xl font-bold text-[#2b6777] mb-6 text-center">{isEdit ? 'Edit Tax' : 'Create New Tax'}</h2>
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Tax Information */}
          <div className='bg-white p-4 rounded-xl shadow-md'>
            <h3 className="text-lg font-bold text-[#2b6777] mb-2">Tax Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className='flex flex-row gap-2 justify-between items-center gap-x-2'>
                <label className="block w-1/4 text-sm text-center font-medium text-[#2b6777] whitespace-nowrap">Tax Code</label>
                <input
                  type="text"
                  value={isLoadingCode ? 'Loading...' : formData.tax_code}
                  onChange={(e) => handleInputChange('tax_code', e.target.value)}
                  className="w-full border rounded px-3 py-2 bg-gray-100 text-gray-700"
                  placeholder="e.g., GST01, VATUK (or auto-generated)"
                  required
                  readOnly
                />
              </div>

              <div className='flex flex-row gap-2 justify-between items-center gap-x-2'>
                <label className="block text-sm text-center font-medium text-[#2b6777] w-1/4 whitespace-nowrap">Tax Type *</label>
                <select
                  value={formData.tax_type}
                  onChange={(e) => handleInputChange('tax_type', e.target.value)}
                  className="w-full border rounded px-3 py-2 border-[#c8d8e4]"
                  required
                >
                  <option value="">Select Tax Type</option>
                  <option value="GST">GST</option>
                  <option value="VAT">VAT</option>
                  <option value="Sales Tax">Sales Tax</option>
                  <option value="Income Tax">Income Tax</option>
                  <option value="Customs Duty">Customs Duty</option>
                  <option value="Excise Duty">Excise Duty</option>
                  <option value="Service Tax">Service Tax</option>
                  <option value="Other">Other</option>
                </select>
              </div>

              <div className='flex flex-row gap-2 justify-between items-center gap-x-2'>
                <label className="block text-sm text-center font-medium text-[#2b6777] w-1/4 whitespace-nowrap">Tax Name *</label>
                <input
                  type="text"
                  value={formData.tax_name}
                  onChange={(e) => handleInputChange('tax_name', e.target.value)}
                  className="w-full border rounded px-3 py-2 border-[#c8d8e4]"
                  placeholder="e.g., Goods and Services Tax"
                  required
                />
              </div>

              <div className='flex flex-row gap-2 justify-between items-center gap-x-2'>
                <label className="block text-sm text-center font-medium text-[#2b6777] w-1/4 whitespace-nowrap">Rate (%) *</label>
                <input
                  type="number"
                  step="0.01"
                  value={formData.rate}
                  onChange={(e) => handleInputChange('rate', e.target.value)}
                  className="w-full border rounded px-3 py-2 border-[#c8d8e4]"
                  placeholder="0.00"
                  min="0"
                  max="100"
                  required
                />
              </div>

              <div className='flex flex-row gap-2 justify-between items-center gap-x-2'>
                <label className="block text-sm text-center font-medium text-[#2b6777] w-1/4 whitespace-nowrap">Country</label>
                <input
                  type="text"
                  value={formData.country}
                  onChange={(e) => handleInputChange('country', e.target.value)}
                  className="w-full border rounded px-3 py-2 border-[#c8d8e4]"
                  placeholder="e.g., India"
                />
              </div>

              <div className='flex flex-row gap-2 justify-between items-center gap-x-2'>
                <label className="block text-sm text-center font-medium text-[#2b6777] w-1/4 whitespace-nowrap">Status *</label>
                <select
                  value={formData.status}
                  onChange={(e) => handleInputChange('status', e.target.value)}
                  className="w-full border rounded px-3 py-2 border-[#c8d8e4]"
                >
                  <option value="Active">Active</option>
                  <option value="Inactive">Inactive</option>
                </select>
              </div>
            </div>
          </div>

          {/* Additional Information */}
          <div className='bg-white p-4 rounded-xl shadow-md'>
            <h3 className="text-lg font-bold text-[#2b6777] mb-2">Additional Information</h3>
            <div className="grid grid-cols-1 gap-4">
              <div className='flex flex-row gap-2 justify-between items-center gap-x-2'>
                <label className="block text-sm text-center font-medium text-[#2b6777] w-1/4 whitespace-nowrap">Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                  className="w-full border rounded px-3 py-2 border-[#c8d8e4]"
                  placeholder="Enter tax description..."
                  rows="3"
                />
              </div>

              <div className="flex items-center gap-x-2">
                <input
                  type="checkbox"
                  id="is_default"
                  checked={formData.is_default}
                  onChange={(e) => handleInputChange('is_default', e.target.checked)}
                  className="w-4 h-4 text-[#52ab98] border-gray-300 rounded focus:ring-[#52ab98] focus:ring-2"
                />
                <label htmlFor="is_default" className="text-sm font-medium text-[#2b6777]">
                  Set as Default Tax
                </label>
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-4 mt-6">
            <button type="button" className="px-4 py-2 rounded bg-gray-200 text-[#2b6777] font-semibold" onClick={onClose}>Cancel</button>
            <button
              type="submit"
              className="px-6 py-2 rounded bg-[#52ab98] hover:bg-[#2b6777] text-white font-semibold shadow disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={loading}
            >
              {loading ? 'Saving...' : (isEdit ? 'Update Tax' : 'Create Tax')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default TaxFormModal; 